import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { statisticsService } from '../services/statisticsService';

export const EventRateChart: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<number>(24);

  const { data, isLoading } = useQuery({
    queryKey: ['eventRates', timeRange],
    queryFn: () => statisticsService.getEventRates(timeRange),
    refetchInterval: 60000,
  });

  const handleTimeRangeChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
    if (newValue !== null) {
      setTimeRange(newValue);
    }
  };

  const chartData = React.useMemo(() => {
    if (!data?.dataPoints) return [];
    return data.dataPoints.map((point) => {
      const timestamp = parseISO(point.timestamp);
      let formattedTime: string;
      if (timeRange <= 6) {
        formattedTime = format(timestamp, 'HH:mm');
      } else if (timeRange <= 24) {
        formattedTime = format(timestamp, 'HH:mm');
      } else if (timeRange <= 168) {
        formattedTime = format(timestamp, 'MMM d HH:mm');
      } else {
        formattedTime = format(timestamp, 'MMM d');
      }
      return { ...point, formattedTime };
    });
  }, [data?.dataPoints, timeRange]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Event Ingestion Rate
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              Avg: {data.averageEventsPerMinute.toFixed(1)} events/min &bull; Total: {data.totalEvents.toLocaleString()}
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          <ToggleButton value={1}>1h</ToggleButton>
          <ToggleButton value={6}>6h</ToggleButton>
          <ToggleButton value={24}>24h</ToggleButton>
          <ToggleButton value={168}>7d</ToggleButton>
          <ToggleButton value={720}>30d</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ width: '100%', height: 300 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Loading...</Typography>
          </Box>
        ) : chartData.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">No data available</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
              <defs>
                <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
              <XAxis
                dataKey="formattedTime"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: 8,
                  fontSize: '0.8125rem',
                }}
              />
              <Area
                type="monotone"
                dataKey="eventCount"
                name="Events/min"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                fill="url(#rateGradient)"
                dot={false}
                activeDot={{ r: 4, fill: theme.palette.primary.main }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};
